import { useSetAtom } from 'jotai';
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Flex,
  Menu,
  MenuContent,
  MenuTrigger,
  Switch,
  Text,
  Textarea,
  toaster,
  Tooltip,
} from '@optiaxiom/react';
import { IconAngleDown, IconCodeBranch, IconDownload, IconSparkles } from '@optimizely/axiom-icons';
import { useDecision } from '@optimizely/react-sdk';

import { workflowAgentAtom } from '@frontend/atoms';
import { useNamespacedNavigate, useOpalUser } from '@frontend/hooks';

import { importSpecializedAgent } from '../../../services/specialized-agent';
import { importWorkflowAgent } from '../../../services/workflow-agent';

export function AgentAddDropdown() {
  const navigate = useNamespacedNavigate();
  const dispatch = useSetAtom(workflowAgentAtom);
  const { opalUser } = useOpalUser();
  const [workflowAgentFeature] = useDecision('workflow-agent');
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importJson, setImportJson] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [importMode, setImportMode] = useState<'skip' | 'overwrite'>('overwrite');

  const handleImportAgent = async () => {
    if (!importJson.trim()) {
      toaster.create('Please paste JSON data', { intent: 'danger' });
      return;
    }

    setIsImporting(true);
    try {
      const agentData = JSON.parse(importJson);

      if (!agentData.agent_type) {
        toaster.create('Invalid JSON: missing agent_type', { intent: 'danger' });
        return;
      }

      if (agentData.agent_type === 'specialized') {
        await importSpecializedAgent(opalUser.instance.instance_id, agentData, importMode)();
        toaster.create('Specialized agent imported successfully', { intent: 'success' });
        navigate('/agents');
      } else if (agentData.agent_type === 'workflow') {
        if (!workflowAgentFeature.enabled) {
          toaster.create('Workflow agents are not available', { intent: 'danger' });
          return;
        }
        await importWorkflowAgent(opalUser.instance.instance_id, agentData, importMode);
        toaster.create('Workflow agent imported successfully', { intent: 'success' });
        navigate('/agents');
      } else {
        toaster.create('Invalid agent_type. Must be "specialized" or "workflow"', {
          intent: 'danger',
        });
        return;
      }

      setIsImportDialogOpen(false);
      setImportJson('');
    } catch (error) {
      console.error('Failed to import agent:', error);
      toaster.create('Failed to import agent. Please check the JSON format.', { intent: 'danger' });
    } finally {
      setIsImporting(false);
    }
  };

  const buildOptions = () => {
    const options = [
      {
        label: 'Specialized Agent',
        description: 'Purpose-built to perform specific, well-defined tasks.',
        execute: () => navigate('/agents/specialized/create'),
        icon: <IconSparkles size="16" />,
      },
    ];

    if (workflowAgentFeature.enabled) {
      options.push({
        label: 'Workflow Agent',
        description: 'Orchestrate multiple agents to perform complex processes.',
        execute: () => {
          navigate('/agents/workflow/create');
          dispatch({ type: 'RESET' });
        },
        icon: <IconCodeBranch size="16" />,
      });
    }

    options.push({
      label: 'Import Agent',
      description: 'Import an agent from JSON data.',
      execute: () => setIsImportDialogOpen(true),
      icon: <IconDownload size="16" />,
    });

    return options;
  };

  return (
    <>
      <Menu options={buildOptions()}>
        <MenuTrigger appearance="primary" addonAfter={<IconAngleDown size="16" />}>
          Add Agent
        </MenuTrigger>
        <MenuContent />
      </Menu>

      <Dialog
        open={isImportDialogOpen}
        onOpenChange={open => {
          setIsImportDialogOpen(open);
          if (!open) {
            setImportJson('');
            setImportMode('overwrite');
          }
        }}
      >
        <DialogContent size="lg">
          <DialogHeader>Import Agent</DialogHeader>
          <DialogBody>
            <Flex flexDirection="column" gap="16">
              <Textarea
                placeholder="Paste your agent JSON data here..."
                value={importJson}
                onChange={e => setImportJson(e.target.value)}
                rows={12}
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
              />
              <Flex flexDirection="column" gap="8">
                <Text fontSize="sm" fontWeight="500">
                  Import Mode
                </Text>
                <Tooltip
                  content={
                    importMode === 'overwrite'
                      ? 'Replace existing agents with the same name/ID'
                      : 'Keep existing agents and skip conflicts'
                  }
                >
                  <Switch
                    checked={importMode === 'overwrite'}
                    onCheckedChange={checked => setImportMode(checked ? 'overwrite' : 'skip')}
                    description={
                      importMode === 'overwrite'
                        ? 'Replace existing agents with the same name/ID'
                        : 'Keep existing agents and skip conflicts'
                    }
                  >
                    {importMode === 'overwrite'
                      ? 'Overwrite existing agents'
                      : 'Skip existing agents'}
                  </Switch>
                </Tooltip>
              </Flex>
            </Flex>
          </DialogBody>
          <DialogFooter>
            <Button
              appearance="subtle"
              onClick={() => {
                setIsImportDialogOpen(false);
                setImportJson('');
                setImportMode('overwrite');
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleImportAgent} disabled={isImporting || !importJson.trim()}>
              {isImporting ? 'Importing...' : 'Import Agent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
